import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API, IMAGE_PATH } from "../../services/api";
import { useNavigate } from "react-router-dom";
import "./ProductCarousel.css";

/* Motion variants – unchanged */
const slideMotion = {
    enter: (direction) => ({
        x: direction > 0 ? 80 : -80,
        opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({
        x: direction > 0 ? -80 : 80,
        opacity: 0,
    }),
};

export default function ProductCarousel() {
    const [slides, setSlides] = useState([]);
    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(1);
    const navigate = useNavigate();

    useEffect(() => {
        async function fetchProducts() {
            try {
                const res = await axios.get(API.getProducts);
                const products = Array.isArray(res.data) ? res.data : [];

                const recent = [...products].sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                );

                const mapped = recent.slice(0, 5).map((p, i) => {
                    let image = "/fallback.avif";
                    if (p.images) {
                        let parsedImages = [];

                        // If already array
                        if (Array.isArray(p.images)) {
                            parsedImages = p.images;
                        }

                        // If string
                        else if (typeof p.images === "string") {
                            try {
                                const parsed = JSON.parse(p.images);
                                if (Array.isArray(parsed)) {
                                    parsedImages = parsed;
                                } else if (p.images.includes(",")) {
                                    parsedImages = p.images.split(",").map(s => s.trim());
                                } else {
                                    parsedImages = [p.images];
                                }
                            } catch {
                                if (p.images.includes(",")) {
                                    parsedImages = p.images.split(",").map(s => s.trim());
                                } else {
                                    parsedImages = [p.images];
                                }
                            }
                        }

                        if (parsedImages.length > 0 && parsedImages[0]) {
                            image = IMAGE_PATH + parsedImages[0];
                        }
                    }
                    return {
                        id: p.id,
                        name: p.productName,
                        price: `₹${Number(p.cost).toLocaleString()}`,
                        oldPrice:
                            Number(p.discountcost) > 0
                                ? `₹${Number(p.discountcost).toLocaleString()}`
                                : "",
                        productImage: image,
                        badge:
                            Number(p.discountcost) > 0 ? "SALE" : "NEW",
                        tag: `CRAFTED IN ${p.purity} SILVER`,
                        quote:
                            p.shortDescription ||
                            "Crafted with precision and elegance.",
                        rightBg: [
                            "#fef08a",
                            "#bbf7d0",
                            "#bfdbfe",
                            "#fde68a",
                        ][i % 4],
                    };
                });

                setSlides(mapped);
            } catch (err) {
                console.error("Carousel fetch error:", err);
            }
        }

        fetchProducts();
    }, []);

    if (!slides.length) return null;

    const slide = slides[index];

    const goNext = () => {
        setDirection(1);
        setIndex((p) => (p + 1) % slides.length);
    };

    const goPrev = () => {
        setDirection(-1);
        setIndex((p) => (p - 1 + slides.length) % slides.length);
    };

    return (
        <section className="fav-wrapper">
            {/* LEFT */}
            <div className="fav-left">
                <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                        key={`left-${slide.id}`}
                        className="fav-blur-bg-left"
                        style={{
                            backgroundImage: `url("${slide.productImage}")`,
                        }}
                        custom={direction}
                        variants={slideMotion}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.45 }}
                    />
                </AnimatePresence>

                <div className="fav-product-card">
                    <span className="fav-badge">{slide.badge}</span>

                    <div className="fav-product-image-wrapper">
                        <img
                            src={slide.productImage}
                            alt={slide.name}
                            className="fav-product-image"
                            onError={(e) => {
                                e.currentTarget.src = "/fallback.avif";
                            }}
                        />
                        <button
                            className="fav-buy-now-btn"
                            onClick={() =>
                                navigate(`/product/${slide.id}`)
                            }
                            
                        >
                            Buy Now
                        </button>
                    </div>

                    <div className="fav-product-info">
                        <p className="fav-tag">{slide.tag}</p>
                        <h3 className="fav-product-name">
                            {slide.name}
                        </h3>

                        <div className="fav-price-row">
                            <span className="fav-price">
                                {slide.cost}
                            </span>
                            {slide.oldPrice && (
                                <span className="fav-old-price">
                                    {slide.oldPrice}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT */}
            <div className="fav-right">
                <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                        key={`right-${slide.id}`}
                        className="fav-right-slide-wrapper"
                        custom={direction}
                        variants={slideMotion}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.45 }}
                    >
                        <div
                            className="fav-blur-bg-right"
                            style={{
                                backgroundColor: slide.rightBg,
                            }}
                        />
                        <div
                            className="fav-right-overlay"
                            style={{
                                backgroundColor: slide.rightBg,
                            }}
                        />

                        <div className="fav-right-content">
                            <div className="fav-right-inner">
                                <p className="fav-right-label">
                                    Our Favorite Products
                                </p>
                                <h2 className="fav-quote">
                                    “{slide.quote}”
                                </h2>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>

                <div className="fav-controls-fixed">
                    <button className="fav-arrow-btn" onClick={goPrev}>
                        &#10094;
                    </button>
                    <span className="fav-counter">
                        {index + 1}/{slides.length}
                    </span>
                    <button className="fav-arrow-btn" onClick={goNext}>
                        &#10095;
                    </button>
                </div>
            </div>

            {/* RIGHT STRIP */}
            <div className="fav-right-side">
                <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                        key={`strip-${slide.id}`}
                        className="fav-right-side-blur"
                        style={{
                            backgroundImage: `url("${slide.productImage}")`,
                        }}
                        custom={direction}
                        variants={slideMotion}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.45 }}
                    />
                </AnimatePresence>
            </div>
        </section>
    );
}
